import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { Phone, MapPin, Clock, ArrowRight, CheckCircle2, Star, Quote } from "lucide-react";
import { LOCAL_AREAS, SERVICES, NAP, BOOK_URL, SITE_URL, pickTestimonial } from "../lib/data";
import CallToActionStrip from "../components/CallToActionStrip";
import FAQ from "../components/FAQ";
import GoogleMap from "../components/GoogleMap";
import { useSEO } from "../lib/useSEO";
import { trackBookClick } from "../lib/analytics";
import { buildBreadcrumbs } from "../lib/breadcrumbs";

export default function LocalArea() {
  const { slug } = useParams();
  const area = LOCAL_AREAS.find((a) => a.slug === slug);
  const testimonial = area ? pickTestimonial(area.slug) : null;
  useSEO({
    title: area ? `${area.name} Appliance Repair — Same-Day Service | Act Fast` : "Local Area",
    description: area
      ? `${area.intro} Call (773) 888-4663 for same-day ${area.city} appliance repair with a full parts & labor warranty.`
      : "Local appliance repair service area",
    path: `/areas/${slug || ""}`,
    jsonLd: area ? buildBreadcrumbs([
      { name: "Home", path: "/" },
      { name: "Service Areas", path: "/contact" },
      { name: area.name, path: `/areas/${area.slug}` },
    ]) : undefined,
  });

  // City-specific LocalBusiness JSON-LD with embedded review + serviceArea.
  // The actual office address remains Chicago (truthful for Google);
  // city-specific signal is delivered via the localized `name`, `areaServed`,
  // `serviceArea` (Postal-code list), and the embedded review snippet.
  useEffect(() => {
    if (!area) return;
    const id = `af-localbusiness-${area.slug}`;
    const existing = document.getElementById(id);
    if (existing) existing.remove();
    const script = document.createElement("script");
    script.id = id;
    script.type = "application/ld+json";
    const schema = {
      "@context": "https://schema.org",
      "@type": "LocalBusiness",
      "@id": `${SITE_URL}/areas/${area.slug}#localbusiness`,
      name: `Act Fast Appliance Repair — ${area.city}, IL`,
      alternateName: "Act Fast Appliance Repair",
      image: "https://customer-assets.emergentagent.com/job_appliance-repair-chi/artifacts/8nnns8mh_act%20fast%20logo.png",
      telephone: NAP.phoneDisplay,
      email: NAP.email,
      url: `${SITE_URL}/areas/${area.slug}`,
      address: {
        "@type": "PostalAddress",
        streetAddress: NAP.addressLine1,
        addressLocality: NAP.city,
        addressRegion: NAP.state,
        postalCode: NAP.zip,
        addressCountry: "US",
      },
      areaServed: {
        "@type": "City",
        name: area.city,
        containedInPlace: { "@type": "State", name: "Illinois" },
      },
      serviceArea: (area.zips || [area.zip]).map((z) => ({
        "@type": "PostalCodeSpecification",
        postalCode: z,
        addressCountry: "US",
        addressRegion: "IL",
      })),
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: `${area.city} Appliance Repair Services`,
        itemListElement: SERVICES.map((s) => ({
          "@type": "Offer",
          itemOffered: { "@type": "Service", name: s.short },
        })),
      },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
          opens: "09:00",
          closes: "17:00",
        },
      ],
      priceRange: "$$",
    };
    if (testimonial) {
      schema.review = {
        "@type": "Review",
        author: { "@type": "Person", name: `${testimonial.name}, ${area.city}` },
        reviewBody: testimonial.quote,
        reviewRating: {
          "@type": "Rating",
          ratingValue: String(testimonial.rating),
          bestRating: "5",
          worstRating: "1",
        },
      };
    }
    script.text = JSON.stringify(schema);
    document.head.appendChild(script);
    return () => {
      const el = document.getElementById(id);
      if (el) el.remove();
    };
  }, [area, testimonial]);

  if (!area) return <Navigate to="/contact" replace />;

  return (
    <div className="bg-black">
      <section className="relative pt-32 pb-12 lg:pt-44 lg:pb-20 border-b border-[#262626]">
        <div className="absolute inset-0 af-grid-noise opacity-40" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <Link to="/contact" className="text-xs uppercase tracking-widest text-neutral-500 hover:text-[#DC2626] mb-6 inline-block">← All Service Areas</Link>
          <p className="af-eyebrow mb-4 flex items-center gap-2"><MapPin size={14} /> Local Service Area</p>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold font-display leading-[0.95] mb-6">
            <span className="text-[#DC2626]">{area.city.toUpperCase()}</span> <br />
            APPLIANCE REPAIR.
          </h1>
          <p className="text-lg text-neutral-300 max-w-3xl leading-relaxed mb-8">{area.intro}</p>
          <div className="flex flex-wrap gap-4">
            <a href={NAP.phoneTel} className="af-btn-primary">
              <Phone size={18} /> Call {NAP.phoneDisplay}
            </a>
            <a
              href={BOOK_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => trackBookClick(`area-${area.slug}`)}
              className="af-btn-outline"
              data-testid="local-book-online"
            >
              Book Online <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24 border-b border-[#262626]">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Stat icon={Clock} label="Avg. Response" value={`${area.travelMinutes} min`} />
          <Stat icon={MapPin} label="Primary ZIP" value={area.zip} />
          <Stat icon={CheckCircle2} label="Warranty" value="Parts & Labor Coverage" />
        </div>
      </section>

      {testimonial && (
        <section className="py-16 lg:py-24 border-b border-[#262626] bg-[#050505]" data-testid="area-testimonial">
          <div className="max-w-4xl mx-auto px-5 lg:px-8 text-center">
            <p className="af-eyebrow mb-4">What {area.city} Homeowners Say</p>
            <Quote size={36} className="text-[#DC2626] mx-auto mb-5" />
            <blockquote className="text-xl lg:text-2xl font-display leading-relaxed text-white mb-6">
              "{testimonial.quote}"
            </blockquote>
            <div className="flex items-center justify-center gap-1 mb-3">
              {Array.from({ length: testimonial.rating }).map((_, i) => (
                <Star key={i} size={18} className="text-[#DC2626] fill-[#DC2626]" />
              ))}
            </div>
            <div className="text-sm text-neutral-400 uppercase tracking-widest">
              — {testimonial.name}, {area.city} homeowner
            </div>
          </div>
        </section>
      )}

      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <p className="af-eyebrow mb-3">Neighborhoods We Serve in {area.city}</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-display mb-6">
              Covering Every Corner of <span className="text-[#DC2626]">{area.city}</span>
            </h2>
            <p className="text-neutral-300 leading-relaxed mb-6">{area.landmarkLine}</p>
            <div className="flex flex-wrap gap-2 mb-12">
              {area.neighborhoods.map((n) => (
                <span key={n} className="af-pill text-sm">{n}</span>
              ))}
            </div>

            <p className="af-eyebrow mb-3">Services Available in {area.city}</p>
            <h2 className="text-3xl sm:text-4xl font-bold font-display mb-6">
              The Full <span className="text-[#DC2626]">Act Fast</span> Lineup
            </h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {SERVICES.map((s) => {
                const Icon = s.icon;
                return (
                  <Link key={s.slug} to={`/services/${s.slug}`} className="af-card p-5 flex items-center gap-3 group">
                    <Icon size={22} className="text-[#DC2626] shrink-0" />
                    <div className="flex-1">
                      <div className="font-bold text-white font-display">{s.short}</div>
                      <div className="text-xs text-neutral-500 uppercase tracking-wider mt-0.5">Available in {area.city}</div>
                    </div>
                    <ArrowRight size={16} className="text-neutral-600 group-hover:text-[#DC2626] group-hover:translate-x-1 transition-all" />
                  </Link>
                );
              })}
            </div>
          </div>

          <aside className="lg:col-span-5">
            <div className="sticky top-28 af-card p-7">
              <p className="af-eyebrow mb-3">{area.city} Dispatch</p>
              <h3 className="text-2xl font-bold font-display mb-4">Same-Day Service in {area.city}</h3>
              <p className="text-neutral-400 text-sm mb-6">Average tech arrival in {area.travelMinutes} minutes from our Chicago hub. Call before noon for same-day service.</p>
              <a href={NAP.phoneTel} className="af-btn-primary w-full justify-center mb-3">
                <Phone size={18} /> {NAP.phoneDisplay}
              </a>
              <a
                href={BOOK_URL}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => trackBookClick(`area-sidebar-${area.slug}`)}
                className="af-btn-outline w-full justify-center"
              >
                Book Online <ArrowRight size={16} />
              </a>
              <div className="mt-6 pt-6 border-t border-[#262626] space-y-2 text-sm text-neutral-400">
                <div>✓ Licensed in {area.city}, IL</div>
                <div>✓ Same-day appointments</div>
                <div>✓ Parts & labor warranty</div>
                <div>✓ Sub-Zero, Wolf, Viking specialists</div>
              </div>
            </div>
          </aside>
        </div>
      </section>

      <GoogleMap />
      <CallToActionStrip />
      <FAQ />
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="af-card p-6 flex items-center gap-4">
      <div className="w-12 h-12 flex items-center justify-center bg-[#141414] border border-[#DC2626] shrink-0">
        <Icon size={22} className="text-[#DC2626]" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-neutral-500 mb-1">{label}</div>
        <div className="text-xl font-bold font-display">{value}</div>
      </div>
    </div>
  );
}
